import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useCallback } from "react";

import {
  getDataDogApplicationId,
  getDataDogClientToken,
  getDataDogEnvironment,
} from "../utils/teamsHelpers";

export const useDatadog = (service: string, sampleRate: number, replaySampleRate: number) => {
  const dataDogEnv = getDataDogEnvironment();

  const clientToken = getDataDogClientToken();

  const initialize = useCallback((identity: string) => {
    datadogRum.init({
      applicationId: getDataDogApplicationId(),
      clientToken,
      site: "datadoghq.com",
      service,
      env: dataDogEnv,
      silentMultipleInit: true,
      sessionSampleRate: sampleRate,
      sessionReplaySampleRate: replaySampleRate,
      trackInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      trackFrustrations: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: "mask-user-input",
      useCrossSiteSessionCookie: true, // This is required for RUM to work in an iFrame context
    });

    const logsInitialized = datadogLogs.getInitConfiguration();
    if (!logsInitialized) {
      datadogLogs.init({
        site: "datadoghq.com",
        forwardErrorsToLogs: true,
        sessionSampleRate: sampleRate,
        clientToken,
        env: dataDogEnv,
      });
    }

    datadogRum.setUser({
      id: identity,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSessionRecording = useCallback(() => {
    if (dataDogEnv === "prod") {
      datadogRum.startSessionReplayRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopSessionRecording = useCallback(() => {
    datadogRum.stopSessionReplayRecording();
  }, []);

  const log = useCallback(
    ({
      logLevel,
      message,
      context,
    }: {
      logLevel: "debug" | "info" | "warn" | "error";
      message: string;
      context?: object;
    }) => {
      switch (logLevel) {
        case "debug":
          datadogLogs.logger.debug(message, context);
          break;
        case "info":
          datadogLogs.logger.info(message, context);
          break;
        case "warn":
          datadogLogs.logger.warn(message, context);
          break;
        case "error":
          datadogLogs.logger.error(message, context);
          break;
      }
    },
    []
  );

  return { initialize, startSessionRecording, stopSessionRecording, log };
};
